import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePlans } from "@/hooks/usePlans";
import "./ComecarPage.css";

// Página de destino do tráfego pago (Meta e Google Ads).
//
// Diferente da landing institucional: uma promessa, uma ação, nenhuma saída.
// Não há menu nem âncoras para outras páginas — quem chega aqui veio de um
// anúncio e tem uma decisão a tomar. O cadastro acontece nesta própria página,
// acima da dobra, porque cada passo extra até a conta custa conversão.

// ─── Teste A/B do título ───────────────────────────────────────────────────
//
// Uma variável só: promessa do título e a linha que a sustenta. Mudar mais de
// uma coisa por vez daria diferença de conversão sem dizer o que causou.
// A variante fica gravada no navegador para o mesmo visitante não ver páginas
// diferentes a cada visita — e viaja em todos os eventos de conversão.
export const VARIANTES = {
  a: {
    titulo: ["Nunca mais perca ", "um prazo", "."],
    linha:
      "O TUTO lê o Diário de Justiça a cada 30 minutos, encontra as publicações da sua " +
      "OAB e coloca o prazo na sua agenda — antes de você abrir o computador.",
  },
  b: {
    titulo: ["A IA que lê suas intimações e ", "lança seus prazos", "."],
    linha:
      "Cada publicação da sua OAB é lida assim que sai, o tipo do ato é identificado e o " +
      "prazo entra na sua agenda. Você confere; o trabalho braçal já foi feito.",
  },
};

const CHAVE_VARIANTE = "tuto_variante_lp";

export function escolherVariante(busca, guardada, sorteio = Math.random()) {
  // O anúncio pode fixar a variante com ?v=a ou ?v=b — útil para QA e para
  // campanhas que precisam mandar tráfego só para uma delas.
  const forcada = new URLSearchParams(busca || "").get("v");
  if (forcada && VARIANTES[forcada]) return forcada;
  if (guardada && VARIANTES[guardada]) return guardada;
  return sorteio < 0.5 ? "a" : "b";
}

export function variantePersistida(busca) {
  let guardada = null;
  try {
    guardada = localStorage.getItem(CHAVE_VARIANTE);
  } catch {
    // Navegação privada: sorteia de novo, o que é aceitável.
  }
  const escolhida = escolherVariante(busca, guardada);
  try {
    localStorage.setItem(CHAVE_VARIANTE, escolhida);
  } catch {
    // Sem storage o teste ainda roda; só não fixa entre visitas.
  }
  return escolhida;
}

const TICKS = [
  { hora: "08:00", texto: "O Diário publica. Você está numa audiência.", alerta: "nao" },
  { hora: "08:12", texto: "O TUTO lê a publicação e identifica sua OAB.", alerta: "nao" },
  { hora: "08:13", texto: "O prazo aparece na sua agenda e chega no seu e-mail.", alerta: "sim" },
  { hora: "a cada 30 min", texto: "E recomeça — inclusive no fim de semana.", alerta: "nao" },
];

const TELAS = [
  { src: "/landing/intimacoes.png", alt: "Painel de intimações do TUTO com publicações do DJEN classificadas", legenda: "Cada publicação das suas OABs, lida e classificada." },
  { src: "/landing/agenda.png", alt: "Agenda jurídica do TUTO com prazos no calendário", legenda: "O prazo já entra na agenda — sem você lembrar." },
  { src: "/landing/processos.png", alt: "Painel de processos do TUTO com processos de todos os tribunais", legenda: "Seus processos de qualquer tribunal, sincronizados." },
];

const respostas = (preco) => [
  {
    titulo: preco ? `${preco} por mês, sem fidelidade` : "Mensal, sem fidelidade",
    texto: "O plano START inclui até 100 processos, 1 OAB e 2 usuários. Você cancela quando quiser, sem multa e sem precisar ligar para ninguém.",
  },
  {
    titulo: "Você não cadastra senha de tribunal",
    texto: "O monitoramento usa as fontes oficiais. Nenhuma credencial do PJe, do e-SAJ ou de qualquer outro sistema passa por aqui.",
  },
  {
    titulo: "Todos os tribunais do país",
    texto: "Justiça estadual, federal e do trabalho, além dos superiores. Não importa qual sistema o seu tribunal usa.",
  },
  {
    titulo: "Configurar leva dois minutos",
    texto: "Você cadastra o número da sua OAB e o monitoramento começa. O primeiro resultado costuma aparecer em minutos.",
  },
];

// Eventos de conversão. Meta e Google só recebem se as tags estiverem na página;
// sem elas, nada quebra e o dataLayer guarda o evento para o GTM.
// Indireção para o teste poder observar o redirecionamento: o jsdom não
// permite substituir window.location.
export const navegador = {
  ir: (url) => window.location.assign(url),
};


export function registrarEvento(nome, dados = {}) {
  try {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: nome, ...dados });
    if (typeof window.fbq === "function") {
      window.fbq("track", nome === "lp_cadastro_concluido" ? "CompleteRegistration" : "Lead", dados);
    }
    if (typeof window.gtag === "function") {
      window.gtag("event", nome, dados);
    }
  } catch {
    // Telemetria nunca pode atrapalhar o cadastro.
  }
}

// A origem do clique acompanha o usuário até a conta ser criada.
export function guardarOrigem(busca) {
  const params = new URLSearchParams(busca || "");
  const origem = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "gclid", "fbclid"].forEach((chave) => {
    const valor = params.get(chave);
    if (valor) origem[chave] = valor.slice(0, 200);
  });
  if (Object.keys(origem).length === 0) return null;
  try {
    sessionStorage.setItem("tuto_origem", JSON.stringify(origem));
  } catch {
    // Navegação privada bloqueia o storage; a página segue funcionando.
  }
  return origem;
}

// O erro precisa dizer o que corrigir. Um "tente de novo" diante de e-mail
// inválido faz o visitante desistir sem saber que errou uma letra.
export function mensagemDoErro(falha) {
  const detalhe = falha?.response?.data?.detail;
  if (typeof detalhe === "string" && detalhe.trim()) return detalhe;
  if (detalhe?.message) return detalhe.message;
  if (Array.isArray(detalhe) && detalhe.length) {
    const campo = String(detalhe[0]?.loc?.at(-1) || "");
    if (campo === "email") return "Confira o e-mail: esse endereço não parece válido.";
    if (campo === "password") return "A senha precisa ter pelo menos 6 caracteres.";
    if (campo === "name") return "Escreva seu nome para continuar.";
  }
  if (falha?.response?.status === 409) return "Este e-mail já tem conta. Tente entrar.";
  return "Não foi possível criar a conta agora. Tente de novo.";
}

function Revelar({ children, atraso = 0, ...resto }) {
  const alvo = useRef(null);
  useEffect(() => {
    const no = alvo.current;
    if (!no || typeof IntersectionObserver === "undefined") return undefined;
    // Só esconde quando há JS para mostrar de volta: sem isso, uma falha de
    // script deixaria a página em branco.
    no.classList.add("lp-oculto");
    const observador = new IntersectionObserver(
      ([entrada]) => {
        if (!entrada.isIntersecting) return;
        no.style.transitionDelay = `${atraso}ms`;
        no.classList.remove("lp-oculto");
        observador.disconnect();
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" },
    );
    observador.observe(no);
    return () => observador.disconnect();
  }, [atraso]);
  return <div ref={alvo} data-revelar {...resto}>{children}</div>;
}

function FormularioCadastro({ id, titulo, subtitulo, variante }) {
  const { cadastrarComPagamento } = useAuth();
  const navigate = useNavigate();
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState("");
  const [enviando, setEnviando] = useState(false);

  const enviar = async (evento) => {
    evento.preventDefault();
    if (enviando) return;
    if (senha.length < 6) {
      setErro("A senha precisa ter pelo menos 6 caracteres.");
      return;
    }
    setErro("");
    setEnviando(true);
    registrarEvento("lp_cadastro_enviado", { origem_form: id, variante });
    try {
      // A conta nasce depois do pagamento; aqui só abrimos o checkout.
      const { url } = await cadastrarComPagamento(nome.trim(), email.trim(), senha);
      registrarEvento("lp_checkout_aberto", { origem_form: id, variante });
      navegador.ir(url);
    } catch (falha) {
      setErro(mensagemDoErro(falha));
      setEnviando(false);
    }
  };

  const entrarComGoogle = () => {
    registrarEvento("lp_cadastro_google", { origem_form: id, variante });
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    window.location.href = `${backendUrl}/api/auth/google/login`;
  };

  return (
    <div className="lp-forma" id={id} data-testid={`lp-forma-${id}`}>
      <h2>{titulo}</h2>
      <p>{subtitulo}</p>
      <form onSubmit={enviar} noValidate>
        <label className="lp-campo">
          <span>Seu nome</span>
          <input
            type="text"
            name="name"
            autoComplete="name"
            required
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Como devemos te chamar"
            data-testid={`${id}-nome`}
          />
        </label>
        <label className="lp-campo">
          <span>E-mail</span>
          <input
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="voce@escritorio.com.br"
            data-testid={`${id}-email`}
          />
        </label>
        <label className="lp-campo">
          <span>Senha</span>
          <input
            type="password"
            name="password"
            autoComplete="new-password"
            required
            minLength={6}
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            placeholder="Mínimo de 6 caracteres"
            data-testid={`${id}-senha`}
          />
        </label>

        {erro && <p className="lp-erro" role="alert" data-testid={`${id}-erro`}>{erro}</p>}

        <button type="submit" className="lp-botao" disabled={enviando} data-testid={`${id}-enviar`}>
          {enviando ? "Abrindo o pagamento..." : "Assinar e começar"}
        </button>
      </form>

      <button type="button" className="lp-google" onClick={entrarComGoogle} data-testid={`${id}-google`}>
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.4a5.5 5.5 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.6-5.2 3.6-8.8z" />
          <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3a7.2 7.2 0 0 1-10.7-3.8h-4v3.1A12 12 0 0 0 12 24z" />
          <path fill="#FBBC05" d="M5.3 14.3a7.1 7.1 0 0 1 0-4.6v-3.1h-4a12 12 0 0 0 0 10.8l4-3.1z" />
          <path fill="#EA4335" d="M12 4.8c1.8 0 3.4.6 4.6 1.8l3.5-3.5A12 12 0 0 0 1.3 6.6l4 3.1A7.2 7.2 0 0 1 12 4.8z" />
        </svg>
        Criar conta com o Google
      </button>

      <p className="lp-miudo">
        Ao criar a conta você aceita os <Link to="/termos-de-uso">Termos de Uso</Link> e a{" "}
        <Link to="/politica-de-privacidade">Política de Privacidade</Link>. Já tem conta?{" "}
        <Link to="/login">Entrar</Link>.
      </p>
    </div>
  );
}

export default function ComecarPage() {
  // O preço vem do banco: estava cravado em quatro lugares desta página e
  // ficou desatualizado quando o START mudou de valor no admin. Sem valor de
  // reserva — enquanto a API não responde as frases saem sem o preço, que é
  // melhor do que anunciar um número que pode não ser o cobrado.
  const { precoDeEntrada } = usePlans();
  const preco = precoDeEntrada();

  const heroi = useRef(null);
  const [variante, setVariante] = useState(null);
  const jaContou = useRef(false);
  // A barra só entra depois que o formulário do topo sai da tela: mostrá-la
  // por cima do próprio formulário atrapalharia quem já está preenchendo.
  const [barraVisivel, setBarraVisivel] = useState(false);

  useEffect(() => {
    const alvo = heroi.current;
    if (!alvo || typeof IntersectionObserver === "undefined") return undefined;
    const observador = new IntersectionObserver(
      ([entrada]) => setBarraVisivel(!entrada.isIntersecting),
      { threshold: 0 },
    );
    observador.observe(alvo);
    return () => observador.disconnect();
  }, []);

  useEffect(() => {
    const escolhida = variantePersistida(window.location.search);
    setVariante(escolhida);
    guardarOrigem(window.location.search);
    // Uma visita, um evento: o StrictMode roda o efeito duas vezes e isso
    // dobraria a base de comparação do teste A/B.
    if (!jaContou.current) {
      jaContou.current = true;
      registrarEvento("lp_visualizou", { variante: escolhida });
    }
    document.title = "TUTO — Nunca mais perca um prazo";
    // O app tem fundo claro no body; sem isto, a rolagem elástica do celular
    // mostra uma faixa branca por baixo da página escura.
    document.body.classList.add("lp-corpo");
    return () => document.body.classList.remove("lp-corpo");
  }, []);

  const irParaForma = useCallback(() => {
    heroi.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    heroi.current?.querySelector("input")?.focus({ preventScroll: true });
  }, []);

  // Até o sorteio acontecer usa-se a variante A: o título nunca pisca vazio.
  const conteudo = VARIANTES[variante] || VARIANTES.a;

  return (
    <div className="lp" data-variante={variante || "a"}>
      <header className="lp-largura lp-topo">
        <img src="/logo-tuto.png" alt="TUTO by Kosmos" />
        <span>Monitoramento de intimações do DJEN</span>
      </header>

      <main>
        <section className="lp-largura lp-heroi" ref={heroi}>
          <div className="lp-brilho" aria-hidden="true" />
          <div>
            <h1 data-testid="lp-titulo">
              {conteudo.titulo[0]}
              <em>{conteudo.titulo[1]}</em>
              {conteudo.titulo[2]}
            </h1>
            <p className="lp-linha-fina">{conteudo.linha}</p>
            <ul className="lp-selos">
              {[preco ? `${preco} por mês` : "Mensal", "Cancele quando quiser", "Pronto em 2 minutos"].map((selo) => (
                <li key={selo}>
                  <Check size={16} strokeWidth={2.5} aria-hidden="true" />
                  {selo}
                </li>
              ))}
            </ul>
          </div>
          <FormularioCadastro
            variante={variante}
            id="topo"
            titulo="Crie sua conta"
            subtitulo="Cadastre sua OAB depois de entrar. O monitoramento começa no mesmo dia."
          />
        </section>

        <section className="lp-largura lp-secao">
          <Revelar>
            <h2>A publicação sai quando você não está olhando.</h2>
            <p className="lp-intro">
              É sempre assim: o Diário circula pela manhã, você está em audiência, e o prazo
              começou a correr sem ninguém avisar. O TUTO fecha essa janela.
            </p>
          </Revelar>
          <div className="lp-vigilia">
            <Revelar className="lp-relogio">
              {TICKS.map((t) => (
                <div className="lp-tick" key={t.hora} data-alerta={t.alerta}>
                  <time>{t.hora}</time>
                  <p>{t.texto}</p>
                </div>
              ))}
            </Revelar>
            <Revelar atraso={120}>
              <div className="lp-moldura">
                <img
                  src="/landing/dashboard.png"
                  alt="Painel do TUTO mostrando processos, intimações e prazos do escritório"
                  loading="lazy"
                />
              </div>
            </Revelar>
          </div>
        </section>

        <section className="lp-largura lp-secao">
          <Revelar>
            <h2>O que você vê depois de cadastrar a OAB.</h2>
            <p className="lp-intro">
              Telas reais do produto. Nada aqui é ilustração.
            </p>
          </Revelar>
          <div className="lp-telas">
            {TELAS.map((tela, i) => (
              <Revelar className="lp-tela" key={tela.src} atraso={i * 90}>
                <figure>
                  <img src={tela.src} alt={tela.alt} loading="lazy" />
                  <figcaption>{tela.legenda}</figcaption>
                </figure>
              </Revelar>
            ))}
          </div>
        </section>

        <section className="lp-largura lp-secao">
          <Revelar>
            <h2>O que costuma travar a decisão.</h2>
          </Revelar>
          <div className="lp-respostas">
            {respostas(preco).map((r, i) => (
              <Revelar className="lp-resposta" key={r.titulo} atraso={i * 80}>
                <h3>{r.titulo}</h3>
                <p>{r.texto}</p>
              </Revelar>
            ))}
          </div>
        </section>

        <section className="lp-largura lp-secao lp-fecho">
          <Revelar>
            <h2>Quanto tempo até a próxima publicação da sua OAB?</h2>
            <p className="lp-intro">
              Ela vai sair de qualquer jeito. A diferença é se você fica sabendo hoje ou
              quando o prazo já estiver correndo.
            </p>
          </Revelar>
          <FormularioCadastro
            variante={variante}
            id="fim"
            titulo="Comece agora, leva 2 minutos"
            subtitulo={preco ? `Plano START por ${preco}/mês. Cancele quando quiser.` : "Plano START mensal. Cancele quando quiser."}
          />
        </section>
      </main>

      <footer className="lp-largura lp-rodape">
        <p>TUTO by KOSMOS — plataforma de gestão jurídica para advogados brasileiros.</p>
        <p>
          <Link to="/termos-de-uso">Termos de Uso</Link> · <Link to="/politica-de-privacidade">Política de Privacidade</Link>
        </p>
      </footer>

      <div className={`lp-barra${barraVisivel ? " lp-barra-dentro" : ""}`} aria-hidden={!barraVisivel}>
        <p>{preco ? `${preco}/mês · ` : ""}cancele quando quiser</p>
        <button type="button" onClick={irParaForma} tabIndex={barraVisivel ? 0 : -1} data-testid="lp-barra-cta">
          Criar conta
        </button>
      </div>
    </div>
  );
}
